import { IncomingMessage } from 'http';
import { isDevelopment } from 'utils/environment';

/**
 * Determines if a page request represents direct navigation rather than internal app navigation.
 *
 * This function analyzes the HTTP referer header to distinguish between users who:
 * - **Directly typed the URL** in their browser address bar
 * - **Clicked a link** from an external site
 * - **Refreshed the page** (F5/Ctrl+R)
 * - **Navigated internally** within the application
 *
 * The function implements a multi-layered detection strategy:
 * 1. **No referer**: Indicates direct URL entry or page refresh
 * 2. **External referer**: Referer from a different domain
 * 3. **Self-referer**: Referer pointing to the same URL (page refresh)
 *
 * @param req the {@link IncomingMessage incoming HTTP request} object containing headers and URL information
 * @returns `true` if the request represents direct navigation, `false` if it's internal navigation
 *
 * @example
 * ```typescript
 * // In API route for analytics
 * export default function handler(req: NextApiRequest, res: NextApiResponse) {
 *   const isDirect = isDirectNavigation(req);
 *
 *   // Track different user behaviors
 *   if (isDirect) {
 *     trackEvent('direct_page_access', {
 *       url: req.url,
 *       userAgent: req.headers['user-agent']
 *     });
 *   } else {
 *     trackEvent('internal_navigation', {
 *       url: req.url,
 *       referer: req.headers.referer
 *     });
 *   }
 *
 *   res.status(200).json({ success: true });
 * }
 * ```
 *
 * @remarks
 * - Relies on the `referer` header which may be blocked by some privacy tools or browsers
 * - Handles both HTTP and HTTPS protocols via the `x-forwarded-proto` header
 */
export function isDirectNavigation(req: IncomingMessage) {
  const referer = req.headers.referer;
  const isDirectNavigation = !referer ||
    !referer.includes(req.headers.host || '') ||
    referer === `${req.headers['x-forwarded-proto'] || isDevelopment ? 'http' : 'https'}://${req.headers.host}${req.url}`;
  return isDirectNavigation;
}